import * as React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from "@eduviaos/ui-kit";
import { useDeleteFloorPlan } from "@/lib/seating/floorPlan";

/**
 * Phase SC-22c — confirm before removing the drawn floor plan. Only the
 * layout goes: rooms, seats, bookings and learner seat plans stay, and the
 * live floor and the learner app show the seat list until a new plan is
 * saved. A newer save by someone else makes the reset fail as stale.
 */

export interface ResetFloorPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  branchId: string | null;
  version: number;
  seatCount: number;
  onReset: () => void;
  onStale: () => void;
}

export function ResetFloorPlanDialog({
  open,
  onOpenChange,
  branchId,
  version,
  seatCount,
  onReset,
  onStale,
}: ResetFloorPlanDialogProps) {
  const remove = useDeleteFloorPlan();
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) setError(null);
  }, [open]);

  function confirm() {
    setError(null);
    remove.mutate(
      { branchId, expectedVersion: version },
      {
        onSuccess: () => {
          toast.success("Floor plan reset. Seats are listed by room until you draw a new one.");
          onOpenChange(false);
          onReset();
        },
        onError: (err) => {
          if (err.details?.code === "seat.floor_plan_stale") {
            onOpenChange(false);
            onStale();
            return;
          }
          if (err.status === 404) {
            onOpenChange(false);
            onReset();
            return;
          }
          setError(err.message || "Couldn't reset the floor plan. Please retry.");
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="reset-floor-plan-dialog">
        <DialogHeader>
          <DialogTitle>Reset floor plan?</DialogTitle>
          <DialogDescription>
            This removes the drawn layout of {seatCount} seat{seatCount === 1 ? "" : "s"}. Your
            rooms, seats, bookings and learner seat plans stay. Learners see the seat list until you
            draw and save a new plan.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div
            role="alert"
            className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
          >
            <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
            <span className="flex-1">{error}</span>
          </div>
        ) : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" type="button" disabled={remove.isPending}>
              Keep floor plan
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            onClick={confirm}
            disabled={remove.isPending}
            data-testid="reset-floor-plan-confirm"
          >
            {remove.isPending ? (
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            ) : null}
            Reset floor plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
